import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface TableUrlStateOptions {
  defaultPageSize?: number;
}

interface TableUrlState {
  page: number;
  pageSize: number;
  search: string;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (value: string) => void;
  getFilter: (key: string) => string;
  setFilter: (key: string, value: string) => void;
}

const DEFAULT_PAGE_SIZE = 10;

export function useTableUrlState(options: TableUrlStateOptions = {}): TableUrlState {
  const defaultPageSize = options.defaultPageSize ?? DEFAULT_PAGE_SIZE;
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Number(searchParams.get('pageSize')) || defaultPageSize;
  const search = searchParams.get('q') ?? '';

  // Setters read the latest params through a ref so their identity never
  // changes on navigation. A page that lists a setter in an effect's deps
  // would otherwise re-run that effect on every page change (and an effect
  // that resets to page 1 would undo every Next click).
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;

  const update = useCallback((mutate: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(paramsRef.current);
    mutate(next);
    // Keep the ref current so two setters called in one tick both apply.
    paramsRef.current = next;
    setParamsRef.current(next, { replace: true });
  }, []);

  const setPage = useCallback(
    (next: number) => {
      update((params) => {
        if (next <= 1) params.delete('page');
        else params.set('page', String(next));
      });
    },
    [update],
  );

  const setPageSize = useCallback(
    (size: number) => {
      update((params) => {
        if (size === defaultPageSize) params.delete('pageSize');
        else params.set('pageSize', String(size));
        params.delete('page');
      });
    },
    [update, defaultPageSize],
  );

  const setSearch = useCallback(
    (value: string) => {
      update((params) => {
        if (!value) params.delete('q');
        else params.set('q', value);
        params.delete('page');
      });
    },
    [update],
  );

  // Through the ref too, so it is as stable as the setters. Callers read it
  // during render, which this hook re-runs on every navigation anyway.
  const getFilter = useCallback((key: string) => paramsRef.current.get(key) ?? '', []);

  const setFilter = useCallback(
    (key: string, value: string) => {
      update((params) => {
        if (!value) params.delete(key);
        else params.set(key, value);
        params.delete('page');
      });
    },
    [update],
  );

  return useMemo(
    () => ({ page, pageSize, search, setPage, setPageSize, setSearch, getFilter, setFilter }),
    [page, pageSize, search, setPage, setPageSize, setSearch, getFilter, setFilter],
  );
}
